import { useCallback, useMemo, useState, type DragEvent } from "react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Skeleton } from "@monorepo-apps/ui/src/components/skeleton";

import { useCreateFeature } from "@/application/use-cases/features/use-features.create.queries";
import {
  useFeaturesList,
  useUpdateFeature,
} from "@/application/use-cases/features/use-features.queries";
import {
  useRemoveVerdict,
  useUpsertVerdict,
  useVerdictsByBranch,
} from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import type { FeatureRow } from "@/infrastructure/supabase/database.types";
import { CreateFeatureDialog } from "@/presentation/components/roadmaps/create-feature-dialog";
import type { CreateFeatureFormValues } from "@/presentation/components/roadmaps/create-feature-dialog";
import { FeatureCard, FeatureCardSkeleton } from "@/presentation/components/roadmaps/feature-card";
import { FeatureInfoDialog } from "@/presentation/components/roadmaps/feature-info-dialog";
import { VerdictColumn } from "@/presentation/components/roadmaps/verdict-column";
import {
  getArchitectureNodeById,
  type ArchitectureMapNode,
} from "@/presentation/data/architecture-map-data";
import { DOMAIN_COLOR, DOMAIN_LABEL } from "@/presentation/data/domains";
import { ALL_FEATURES_VERDICTS, type Verdict } from "@/presentation/data/roadmaps";

type Column = Verdict | "stack";
const COLUMNS: readonly Column[] = ["stack", ...ALL_FEATURES_VERDICTS];

interface Props {
  nodeId: string | null;
  onClose: () => void;
}

type SectionId = "bound" | "domain";
const DRAG_MIME = "application/x-feature-id";

export function ArchitectureNodeFeaturesDialog({ nodeId, onClose }: Props) {
  const node = nodeId ? getArchitectureNodeById(nodeId) : null;
  return (
    <Dialog open={node !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[88vh] !w-[min(96vw,1400px)] !max-w-none overflow-y-auto">
        {node ? <DialogBody key={node.id} node={node} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function DialogBody({ node }: { node: ArchitectureMapNode }) {
  const featuresQuery = useFeaturesList();
  const verdictsQuery = useVerdictsByBranch("all-features");
  const updateFeature = useUpdateFeature();
  const upsertVerdict = useUpsertVerdict();
  const removeVerdict = useRemoveVerdict();
  const createFeature = useCreateFeature();

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [overSection, setOverSection] = useState<{
    section: SectionId;
    column: Column;
  } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [infoFeature, setInfoFeature] = useState<FeatureRow | null>(null);

  const allFeatures = featuresQuery.data ?? [];
  const allVerdicts = verdictsQuery.data ?? [];
  const isLoading = featuresQuery.isPending || verdictsQuery.isPending;

  const featuresById = useMemo(() => {
    const map = new Map<string, FeatureRow>();
    for (const f of allFeatures) map.set(f.id, f);
    return map;
  }, [allFeatures]);

  const verdictsByFeature = useMemo(() => {
    const map = new Map<string, Verdict>();
    for (const v of allVerdicts) map.set(v.feature_id, v.verdict);
    return map;
  }, [allVerdicts]);

  // domain isn't a valid value for actors, but we already gated on system nodes.
  const nodeDomain = node.domain === "actors" ? null : node.domain;

  const bound = useMemo(
    () => allFeatures.filter((f) => f.component_id === node.id),
    [allFeatures, node.id]
  );
  const alsoInDomain = useMemo(
    () =>
      nodeDomain === null
        ? []
        : allFeatures.filter((f) => f.domain === nodeDomain && f.component_id !== node.id),
    [allFeatures, nodeDomain, node.id]
  );

  const groupByColumn = useCallback(
    (rows: readonly FeatureRow[]): Map<Column, FeatureRow[]> => {
      const out = new Map<Column, FeatureRow[]>();
      for (const f of rows) {
        const v = verdictsByFeature.get(f.id);
        const key: Column = v ?? "stack";
        const bucket = out.get(key) ?? [];
        bucket.push(f);
        out.set(key, bucket);
      }
      return out;
    },
    [verdictsByFeature]
  );

  const boundByColumn = useMemo(() => groupByColumn(bound), [bound, groupByColumn]);
  const domainByColumn = useMemo(() => groupByColumn(alsoInDomain), [alsoInDomain, groupByColumn]);

  const handleDragStart = useCallback((e: DragEvent<HTMLDivElement>, feature: FeatureRow) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(DRAG_MIME, feature.id);
    e.dataTransfer.setData("text/plain", feature.id);
    setDraggedId(feature.id);
  }, []);

  const handleDragOver = useCallback(
    (section: SectionId) => (e: DragEvent<HTMLDivElement>, column: Column) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setOverSection({ section, column });
    },
    []
  );

  const handleDragLeave = useCallback(() => setOverSection(null), []);

  const handleDrop = useCallback(
    (section: SectionId) => (e: DragEvent<HTMLDivElement>, column: Column) => {
      e.preventDefault();
      setOverSection(null);
      const id =
        e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData("text/plain") || draggedId;
      setDraggedId(null);
      if (!id) return;
      const feature = featuresById.get(id);
      if (!feature) return;

      // Section-driven binding updates
      if (section === "bound" && feature.component_id !== node.id) {
        updateFeature.mutate({
          id,
          patch: { component_id: node.id, domain: nodeDomain ?? feature.domain },
        });
      } else if (section === "domain" && feature.component_id === node.id) {
        updateFeature.mutate({ id, patch: { component_id: null } });
      }

      // Column update — verdict upsert, or removal when dropping back to stack
      const existing = verdictsByFeature.get(id);
      if (column === "stack") {
        if (existing) {
          removeVerdict.mutate({ featureId: id, branch: "all-features" });
        }
      } else if (existing !== column) {
        upsertVerdict.mutate({ featureId: id, branch: "all-features", verdict: column });
      }
    },
    [
      draggedId,
      featuresById,
      node.id,
      nodeDomain,
      removeVerdict,
      updateFeature,
      upsertVerdict,
      verdictsByFeature,
    ]
  );

  const handleAddExisting = useCallback(
    (featureId: string) => {
      if (!featureId) return;
      const feature = featuresById.get(featureId);
      if (!feature) return;
      updateFeature.mutate({
        id: featureId,
        patch: { component_id: node.id, domain: nodeDomain ?? feature.domain },
      });
    },
    [featuresById, node.id, nodeDomain, updateFeature]
  );

  const handleCreate = useCallback(
    (values: CreateFeatureFormValues) => {
      createFeature.mutate(
        {
          input: {
            title: values.title,
            subtitle: values.subtitle,
            description: values.description,
            domain: values.domain ?? undefined,
            component_id: values.component_id ?? node.id,
            journey: values.journey,
          },
          // Architecture context = MVP candidate.
          seedAsMvp: true,
        },
        { onSuccess: () => setCreateOpen(false) }
      );
    },
    [createFeature, node.id]
  );

  const otherFeatures = useMemo(
    () => allFeatures.filter((f) => f.component_id !== node.id),
    [allFeatures, node.id]
  );

  const accent = nodeDomain ? DOMAIN_COLOR[nodeDomain] : "oklch(0.5 0.02 240)";
  const domainLabel = nodeDomain ? DOMAIN_LABEL[nodeDomain] : "—";

  return (
    <>
      <DialogHeader>
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-1 inline-flex size-3 shrink-0 rounded-full"
            style={{ background: accent }}
          />
          <div className="min-w-0 flex-1">
            <DialogTitle className="leading-snug">{node.name}</DialogTitle>
            <DialogDescription className="text-foreground/70 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-[13px]">{node.role}</span>
            </DialogDescription>
            <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
              <span>
                Domain: <span className="text-foreground font-medium">{domainLabel}</span>
              </span>
              <span>
                Ownership: <span className="text-foreground font-medium">{node.ownership}</span>
              </span>
            </div>
          </div>
        </div>
      </DialogHeader>

      <div className="space-y-4 pt-3">
        <Toolbar
          boundCount={bound.length}
          domainCount={alsoInDomain.length}
          onCreate={() => setCreateOpen(true)}
          otherFeatures={otherFeatures}
          onAddExisting={handleAddExisting}
        />

        <Section
          title="Bound to this component"
          count={bound.length}
          accent={accent}
          isLoading={isLoading}
          emptyHint="Drag a feature from below, or use the picker to bind one."
        >
          <Kanban
            section="bound"
            byColumn={boundByColumn}
            overSection={overSection}
            isLoading={isLoading}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver("bound")}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop("bound")}
            onInfo={setInfoFeature}
          />
        </Section>

        <Section
          title={`Also in ${domainLabel}`}
          count={alsoInDomain.length}
          accent={accent}
          muted
          isLoading={isLoading}
          emptyHint={
            nodeDomain
              ? "No other features in this domain."
              : "This node has no domain — nothing to compare."
          }
        >
          <Kanban
            section="domain"
            byColumn={domainByColumn}
            overSection={overSection}
            isLoading={isLoading}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver("domain")}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop("domain")}
            onInfo={setInfoFeature}
          />
        </Section>
      </div>

      <CreateFeatureDialog
        open={createOpen}
        branch="all-features"
        isSubmitting={createFeature.isPending}
        onSubmit={handleCreate}
        onClose={() => setCreateOpen(false)}
        defaultDomain={nodeDomain}
        defaultComponentId={node.id}
        defaultComponentLabel={node.name}
      />

      <FeatureInfoDialog feature={infoFeature} onClose={() => setInfoFeature(null)} />
    </>
  );
}

function Toolbar({
  boundCount,
  domainCount,
  onCreate,
  otherFeatures,
  onAddExisting,
}: {
  boundCount: number;
  domainCount: number;
  onCreate: () => void;
  otherFeatures: readonly FeatureRow[];
  onAddExisting: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-muted-foreground text-[11px]">
        {boundCount} bound · {domainCount} elsewhere in this domain
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <AddExistingPicker features={otherFeatures} onSelect={onAddExisting} />
        <Button type="button" size="sm" onClick={onCreate} className="h-8 px-3 text-[11px]">
          + Create feature here
        </Button>
      </div>
    </div>
  );
}

function AddExistingPicker({
  features,
  onSelect,
}: {
  features: readonly FeatureRow[];
  onSelect: (id: string) => void;
}) {
  return (
    <Select
      onValueChange={(v) => {
        if (v) onSelect(v);
      }}
    >
      <SelectTrigger className="h-8 min-w-[220px] text-[11px]">
        <SelectValue placeholder="Add existing feature…" />
      </SelectTrigger>
      <SelectContent className="max-h-[60vh]">
        {features.length === 0 ? (
          <div className="text-muted-foreground px-3 py-2 text-[11px] italic">
            No other features available.
          </div>
        ) : (
          features.map((f) => (
            <SelectItem key={f.id} value={f.id} className="text-[12px]">
              {f.title}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

function Section({
  title,
  count,
  accent,
  muted = false,
  isLoading = false,
  emptyHint,
  children,
}: {
  title: string;
  count: number;
  accent: string;
  muted?: boolean;
  isLoading?: boolean;
  emptyHint: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-slot="arch-node-features-section"
      data-muted={muted}
      className="rounded-lg border border-[oklch(0.91_0.008_240)] bg-white/60 p-2.5 data-[muted=true]:bg-[oklch(0.97_0.005_240_/_0.5)]"
    >
      <header className="mb-2 flex items-center gap-2 px-1">
        <span aria-hidden className="size-2 rounded-full" style={{ background: accent }} />
        <h3 className="text-foreground text-[12px] font-semibold tracking-tight">{title}</h3>
        {isLoading ? (
          <Skeleton className="h-3 w-8 rounded" />
        ) : (
          <span className="text-muted-foreground font-data text-[10px] tabular-nums">
            ({count})
          </span>
        )}
        {!isLoading && count === 0 ? (
          <span className="text-muted-foreground/80 ml-2 text-[10.5px] italic">{emptyHint}</span>
        ) : null}
      </header>
      <div data-muted={muted} className="data-[muted=true]:opacity-80">
        {children}
      </div>
    </section>
  );
}

function Kanban({
  section,
  byColumn,
  overSection,
  isLoading = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onInfo,
}: {
  section: SectionId;
  byColumn: Map<Column, FeatureRow[]>;
  overSection: { section: SectionId; column: Column } | null;
  isLoading?: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>, feature: FeatureRow) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>, column: Column) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>, column: Column) => void;
  onInfo: (feature: FeatureRow) => void;
}) {
  return (
    <div className="flex gap-3">
      {COLUMNS.map((column) => {
        const features = byColumn.get(column) ?? [];
        const isOver = overSection?.section === section && overSection.column === column;
        if (column === "stack") {
          return (
            <StackColumn
              key={column}
              features={features}
              isOver={isOver}
              isLoading={isLoading}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onCardDragStart={onDragStart}
              onInfo={onInfo}
            />
          );
        }
        return (
          <VerdictColumn
            key={column}
            verdict={column}
            features={features}
            isOver={isOver}
            isLoading={isLoading}
            onDragOver={(e, verdict) => onDragOver(e, verdict)}
            onDragLeave={onDragLeave}
            onDrop={(e, verdict) => onDrop(e, verdict)}
            onCardDragStart={onDragStart}
            onInfo={onInfo}
            onEdit={() => {}}
            onRemove={() => {}}
          />
        );
      })}
    </div>
  );
}

function StackColumn({
  features,
  isOver,
  isLoading = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onCardDragStart,
  onInfo,
}: {
  features: readonly FeatureRow[];
  isOver: boolean;
  isLoading?: boolean;
  onDragOver: (e: DragEvent<HTMLDivElement>, column: Column) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>, column: Column) => void;
  onCardDragStart: (e: DragEvent<HTMLDivElement>, feature: FeatureRow) => void;
  onInfo: (feature: FeatureRow) => void;
}) {
  const accent = "oklch(0.55 0.015 240)";
  const fill = "oklch(0.95 0.008 240)";
  return (
    <div
      data-slot="stack-column"
      data-over={isOver}
      onDragOver={(e) => onDragOver(e, "stack")}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, "stack")}
      className="flex min-w-[260px] flex-1 flex-col rounded-lg border border-dashed border-[oklch(0.88_0.01_240)] bg-white/40 transition-colors"
      style={isOver ? { background: fill, borderColor: accent } : undefined}
    >
      <header
        className="rounded-t-lg border-b border-[oklch(0.91_0.008_240)] px-3 py-2.5"
        style={{ borderTop: `3px solid ${accent}` }}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-foreground text-[13px] font-semibold tracking-tight">
            Feature Stack
          </h3>
          {isLoading ? (
            <Skeleton className="h-3.5 w-5 rounded" />
          ) : (
            <span
              className="font-data inline-flex min-w-5 items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
              style={{ background: `${accent}1a`, color: accent }}
            >
              {features.length}
            </span>
          )}
        </div>
        <p className="text-muted-foreground mt-0.5 text-[10.5px] leading-snug">
          Untriaged on the all-features board
        </p>
      </header>
      <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto p-2.5">
        {isLoading ? (
          <>
            <FeatureCardSkeleton />
            <FeatureCardSkeleton />
            <FeatureCardSkeleton />
          </>
        ) : features.length === 0 ? (
          <div
            className={`flex min-h-32 flex-1 items-center justify-center rounded-md border-2 border-dashed text-center text-[11px] transition-colors ${
              isOver
                ? "border-foreground/40 text-foreground/70"
                : "border-foreground/15 text-muted-foreground/60"
            }`}
          >
            <p className="px-4">{isOver ? "Drop here" : "Untriaged features land here"}</p>
          </div>
        ) : (
          features.map((f) => (
            <FeatureCard
              key={f.id}
              feature={f}
              variant="placed"
              onDragStart={onCardDragStart}
              onInfo={onInfo}
              onEdit={() => {}}
              onRemove={() => {}}
            />
          ))
        )}
      </div>
    </div>
  );
}
